/****************
 * THE NOTIFICATIONS, ONE DEFINITION FOR BOTH APPS.
 *
 * Guillaume, 2026-10-08: "we should not have 2 separate notification set, the
 * notification box should display the exact same notifications on both
 * versions, we should not have to 'compute them' both place we should have them
 * stored in backend and have both of them pointing toward the same thing."
 *
 * The notifications ARE stored in the backend already: every one is a row of
 * replicated_orders, and both apps read the same GET /replicator/orders (no
 * session id = every order of the user, newest first). What differed was what
 * each app did with them: the web took 50, the phone 100; "already read" was a
 * mark kept on each device, so reading on one left the other lit; and each app
 * worded the rows its own way, the phone in English.
 *
 * So everything that decides WHAT is shown lives here, once, and this file is
 * byte-identical in both apps (frontend/src/lib and mobile-app/src/lib; the
 * mobile copy is written by scripts/sync-mobile-locales.mjs, never by hand):
 *  - how many, and how often they are re-read;
 *  - the read mark: ONE number on the account (the user-preferences store both
 *    apps already share), so reading on either clears both;
 *  - which rows count as unread, and the first-visit baseline;
 *  - how a session event is named (keys in messages.json, translated);
 *  - the "5 min ago" rule.
 * Each app only draws it. No i18n or React here: keys and numbers out.
 ****************/

/** How many of the newest notifications both apps show. */
export const NOTIFICATION_FEED_LIMIT = 50;
/** How often both apps re-read them. */
export const NOTIFICATION_POLL_MS = 30_000;
/** Rows drawn at first; more on request (the web's bell, Guillaume 2026-09-03). */
export const NOTIFICATION_PAGE = 5;
/** Rows added by each "show more". */
export const NOTIFICATION_PAGE_STEP = 10;

/** The account preference holding the newest notification id already seen. */
export const NOTIFICATIONS_SEEN_PREF = 'copyfi.notifications.lastSeenId';

interface FeedRow {
  id: number;
}

export function newestNotificationId(rows: readonly FeedRow[] | undefined): number {
  return (rows ?? []).reduce((max, r) => (r.id > max ? r.id : max), 0);
}

export function unreadNotifications(rows: readonly FeedRow[] | undefined, lastSeenId: number): number {
  return (rows ?? []).filter((r) => r.id > lastSeenId).length;
}

/**
 * The mark to store before anything is counted, or null when the stored one
 * stands.
 *
 * A first visit would otherwise light the bell with every past order, which is
 * not news. So an account with no mark yet starts from the newest id: from a
 * mark this device kept before the mark moved to the account (so nobody's
 * unread count jumps or vanishes on the switch), else from the newest row.
 * Only once the account's own value has been read (`loaded`), or a device would
 * overwrite the mark the other app had set.
 */
export function baselineSeenId(
  stored: number,
  loaded: boolean,
  rows: readonly FeedRow[] | undefined,
  legacyDeviceMark = 0,
): number | null {
  if (!loaded || stored > 0) return null;
  const newest = newestNotificationId(rows);
  if (newest === 0) return null;
  return legacyDeviceMark > 0 ? Math.min(legacyDeviceMark, newest) : newest;
}

/* ── How a session event is named ──────────────────────────────────────── */

/**
 * Lifecycle rows (something that happened TO the account, not a trade), as
 * keys in messages.json. The map is the authority; the SESSION_ prefix is the
 * fallback, so an event added later without a name here reads "session
 * updated", which claims nothing, rather than its raw database enum.
 */
export const LIFECYCLE_LABEL_KEYS: Record<string, string> = {
  SESSION_STARTED: 'action.sessionStarted',
  SESSION_STOPPED: 'action.sessionEnded',
  SESSION_SETTINGS_CHANGED: 'action.settingsChanged',
  SESSION_POSITIONS_CLOSED: 'action.positionsClosed',
  SESSION_STOP_REFUSED: 'action.stopRefused',
  SESSION_KEY_REJECTED: 'action.keyRejected',
  SESSION_KEY_EXPIRING: 'action.keyExpiring',
};
export const LIFECYCLE_FALLBACK_KEY = 'action.sessionUpdated';

export function isLifecycleAction(action?: string | null): boolean {
  const raw = (action || '').toUpperCase();
  return raw in LIFECYCLE_LABEL_KEYS || raw.startsWith('SESSION_');
}

/**
 * What a row's action is called: a messages.json key for a session event, the
 * action itself for a trade (OPEN, CLOSE, INCREASE, DECREASE are the words the
 * rest of the product uses), or null when there is none.
 */
export function actionLabelOf(action?: string | null): { key: string } | { text: string } | null {
  const raw = (action || '').toUpperCase();
  if (!raw) return null;
  if (isLifecycleAction(raw)) return { key: LIFECYCLE_LABEL_KEYS[raw] ?? LIFECYCLE_FALLBACK_KEY };
  return { text: raw };
}

/* ── "5 min ago" ───────────────────────────────────────────────────────── */

/** The common.json key and count for how long ago `iso` was, or null. */
export function timeAgoOf(iso: string | null | undefined, nowMs: number): { key: string; n?: number } | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((nowMs - then) / 60000);
  if (mins < 1) return { key: 'bell.ago.justNow' };
  if (mins < 60) return { key: 'bell.ago.minutes', n: mins };
  const hours = Math.floor(mins / 60);
  if (hours < 24) return { key: 'bell.ago.hours', n: hours };
  return { key: 'bell.ago.days', n: Math.floor(hours / 24) };
}

/**
 * The bell's one list (Guillaume 11-03: "Backtest result should be mixed with the rest of the
 * notifications and everything should be filtered by Recency"): a backtest's notice and the
 * orders together, newest first. A row with no readable time goes last.
 */
export type FeedItem<N, O> = { kind: 'notice'; n: N; at: number } | { kind: 'order'; o: O; at: number };
export function mergeFeed<N extends { created_at: string }, O extends { created_at?: string }>(
  notices: readonly N[],
  orders: readonly O[],
): FeedItem<N, O>[] {
  const t = (s?: string) => { const v = s ? Date.parse(s) : NaN; return Number.isFinite(v) ? v : -Infinity; };
  const all: FeedItem<N, O>[] = [
    ...notices.map((n) => ({ kind: 'notice' as const, n, at: t(n.created_at) })),
    ...orders.map((o) => ({ kind: 'order' as const, o, at: t(o.created_at) })),
  ];
  return all.sort((a, b) => b.at - a.at);
}
